<template>
  <div>
    <v-select
      :id="snInputProps_identifier"
      v-model="response"
      :attach="snInputProps_attach"
      :class="snInputProps_classes"
      :deletable-chips="snInputProps_deletableChips"
      :data-cy="(snInputProps_identifier || snInputProps_placeholder) + '-dropdown'"
      :placeholder="snInputProps_disabled ? null : snInputProps_placeholder"
      :autofocus="snInputProps_autofocus"
      :disabled="snInputProps_disabled"
      :error-messages="snInputProps_errorMessages"
      :aria-label="snInputProps_label"
      :hide-details="snInputProps_hideDetails"
      :items="optionObjects"
      :item-text="itemText"
      :item-value="itemValue"
      :menu-props="snInputProps_smallChips ? { bottom: true, offsetY: true } : { nudgeTop: -48 }"
      :multiple="snInputProps_multiple"
      :name="snInputProps_name"
      :small-chips="snInputProps_smallChips"
      item-color="snPrimary"
      loader-height="100"
      autocomplete="nope"
      append-icon="sn-icon-chevron-down-small"
      rounded
      outlined
      :return-object="returnObject"
      :rules="snInputProps_rules"
      :validate-on-blur="snInputProps_validateOnBlur"
      @change="$emit('change', $event)"
      @blur="$emit('blur')"
      @click.native="$emit('click.native')"
    >
      <template
        v-if="hasItemSlot"
        v-slot:item="data"
      >
        <slot
          name="item"
          :data="data"
        />
      </template>
      <template
        v-if="hasPrependInnerSlot"
        v-slot:prepend-inner
      >
        <slot name="prepend-inner" />
      </template>
    </v-select>
  </div>
</template>

<script>
import SnInputProps from '../../props/SnInputProps'
import deprecatedMixin from '~/components/snui/v2/_deprecated/deprecatedMixin.vue'

export default {
  name: 'SnSelectDeprecated',
  mixins: [deprecatedMixin],
  props: {
    ...SnInputProps,
    options: {
      type: Array,
      required: true,
      default () {
        return []
      }
    },
    itemText: {
      type: String,
      default: 'text',
      required: false
    },
    itemValue: {
      type: String,
      default: 'value',
      required: false
    },
    returnObject: {
      type: Boolean,
      default: false,
      required: false
    },
    value: {
      type: [String, Number, Array, Object],
      default: '',
      required: false
    }
  },
  data () {
    return {
      response: '',
      optionObjects: [],
      deprecatedMixin: {
        alternativeComponent: 'SnSelect'
      }
    }
  },
  computed: {
    hasItemSlot () {
      return !!this.$scopedSlots.item
    },
    hasPrependInnerSlot () {
      return !!this.$slots['prepend-inner']
    }
  },
  watch: {
    value (newVal) {
      this.response = newVal
    },
    response (newVal) {
      this.$emit('input', this.response)
    }
  },
  created () {
    this.response = this.value
    if (this.options.length && !this.options[0][this.itemValue] && !(typeof this.options[0] === 'object')) {
      const optionObjects = []
      this.options.map((option) => {
        const obj = { disabled: false }
        obj[this.itemValue] = option
        obj[this.itemText] = option
        optionObjects.push(obj)
      })
      this.optionObjects = optionObjects
    } else if (this.options.length) {
      this.optionObjects = this.options
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui/index.scss';
@import '~/assets/css/snui/_snColorVariables.scss';

.optional-label {
  color: #BFBFBF;
}

.v-input--is-disabled {
  ::v-deep .v-input__slot {
    background-color: $sn-gray-01 !important;
    color: $sn-primary-01;
    fieldset {
      border: 1px solid $sn-gray-04;
    }
  }
}
::v-deep .v-input--is-disabled {
  .v-input__append-inner {
    display: none;
  }
}

::v-deep {
  font-family: $font-family-primary !important;

  .v-select__selections {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  .v-select__selection {
    margin: 0 !important;
  }

  .v-input__slot {
    background-color: $sn-white !important;
    padding: 0 12px !important;
    box-sizing: border-box;
    border-radius: 2px !important;
    min-height: 48px !important;
    cursor: pointer !important;

    fieldset {
      color: rgba(0,0,0,.38);
      border: 1px solid !important;
    }

    &:hover, &:focus {
      fieldset {
        color: rgba(0, 0, 0, .86);
      }
    }

    input {
      font-size: 1.063em !important;
      cursor: pointer !important;
    }

    input::placeholder {
      color: $sn-primary-01 !important;
    }

    .v-input__append-inner {
      align-self: center;
      margin-top: 0px !important;
      // margin-top: 12px !important;
    }

    .v-icon {
      color: $sn-primary !important;
    }
  }

  .v-text-field__details {
    padding: 0 !important;
  }

  .v-select__selection {
    overflow: visible;
  }

  .v-messages {
    min-height: 22px;

    .v-messages__message {
      @extend .sn-body-2;
      color: $sn-primary-01;
      display: flex;
      align-content: center;
      line-height: inherit;

      &.message-transition-leave-active, &.message-transition-leave-to {
        display: none;
      }
    }

    &.error--text .v-messages__message {
      color: $sn-error;

      &:before {
        font-family: SimpleNexusIcons !important;
        font-size: 18px;
        width: 24px;
        text-align: center;
        content: "\e972";
        margin-right: 4px;
      }
    }

    &.error--text .v-messages__wrapper {
      display: inline-flex;
      padding-bottom: 2px;
    }
  }

  .error--text {
    fieldset {
      color: $sn-error !important;
    }
  }

  .v-list-item {
    height: 56px;

    .v-list-item__title {
      font-size: 17px;
      font-weight: $font-weight-regular;
      line-height: 24px;
    }
  }
}

::v-deep .v-list-item--active {
  background-color: $sn-brand-01 !important;

  &:before {
    opacity: 0;
  }
}

::v-deep .v-list-item--link:hover {
  background-color: $sn-brand-01 !important;

  &:before {
    opacity: 0;
  }
}

::v-deep .v-list {
  padding: 0px !important;
}

::v-deep .v-menu__content {
  z-index: 100 !important;
  box-shadow: $sn-shadow-depth-3;
}

::v-deep input::placeholder {
  color: $sn-primary-04 !important;
}
</style>
